import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, X, Check, Car, Bike, Anchor, Home, Cross, Sparkles, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLangPrefix, withLangPrefix } from "../hooks/useLangPrefix";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "automobile", label: "Sellerie automobile", subtitle: "Sièges, banquettes, ciel de toit…", Icon: Car },
  { key: "moto", label: "Sellerie moto", subtitle: "Selles, dosserets, sacoches sur-mesure", Icon: Bike },
  { key: "nautique", label: "Sellerie nautique", subtitle: "Coussins, bâches, banquettes bateau", Icon: Anchor },
  { key: "habitat", label: "Habitat / mobilier", subtitle: "Fauteuils, canapés, têtes de lit", Icon: Home },
  { key: "medical", label: "Sellerie médicale", subtitle: "Tables d'examen, fauteuils dentaires", Icon: Cross },
  { key: "autre", label: "Autre projet sur-mesure", subtitle: "Décris ton besoin librement", Icon: Sparkles },
];

const STEPS = [
  { key: "category", label: "Projet" },
  { key: "brief", label: "Brief" },
  { key: "delivery", label: "Livraison" },
  { key: "inspiration", label: "Inspirations" },
  { key: "contact", label: "Coordonnées" },
];

const MAX_IMAGES = 5;
const MAX_BYTES = 5 * 1024 * 1024;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const QuoteRequestPage = ({ mode = "school" }) => {
  const isNetwork = mode === "network";
  const { lang } = useLangPrefix();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    category: "",
    category_precision: "",
    project_description: "",
    quantity: "",
    delivery_date: "",
    delivery_flexibility: "flexible",
    budget_range: "",
    full_name: "",
    email: "",
    phone: "",
    city: "",
    company: "",
  });
  const [images, setImages] = useState([]);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const canNext = useMemo(() => {
    if (step.key === "category") return !!form.category;
    if (step.key === "brief") return form.project_description.trim().length >= 15;
    if (step.key === "delivery") return true; // optional
    if (step.key === "inspiration") return true; // optional
    if (step.key === "contact") return form.full_name.trim().length > 1 && EMAIL_RE.test(form.email);
    return true;
  }, [step, form]);

  const setField = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const onImages = (files) => {
    const arr = Array.from(files || []);
    const merged = [...images];
    for (const f of arr) {
      if (merged.length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images.`);
        break;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" dépasse 5 Mo.`);
        continue;
      }
      if (!["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"].includes(f.type)) {
        toast.error(`"${f.name}" : format non supporté.`);
        continue;
      }
      merged.push(f);
    }
    setImages(merged);
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append("source", isNetwork ? "network" : "school");
      images.forEach((f) => fd.append("images", f));
      const r = await fetch(`${API}/api/quotes`, { method: "POST", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur lors de l'envoi.");
      }
      setDone(true);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] bg-[#FAF7F0] flex items-center justify-center px-6 py-20">
        <div className="max-w-lg text-center" data-testid="quote-success">
          <div className="w-20 h-20 rounded-full bg-[#1F2A4A] text-[#C9B896] mx-auto flex items-center justify-center mb-6">
            <Check className="w-9 h-9" strokeWidth={2} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">Demande envoyée</p>
          <h1 className="font-light text-3xl md:text-4xl text-[#1F2A4A] leading-tight mb-4">
            Merci, votre demande <span className="font-serif italic">est bien arrivée.</span>
          </h1>
          <p className="text-[#4A5568] font-light leading-relaxed mb-8">
            Un récapitulatif vient de vous être envoyé par email. Notre atelier vous contactera sous <b>48 h ouvrées</b> avec une proposition personnalisée.
          </p>
          <Button onClick={() => navigate(withLangPrefix("/", lang))} className="rounded-full px-8 h-11 bg-[#1F2A4A] hover:bg-[#141C2E] text-white">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-[#FAF7F0]" data-testid="quote-request-page">
      {/* Header band */}
      <section className="pt-16 pb-10 md:pt-24 md:pb-14 bg-[#1F2A4A] text-[#FAF7F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">
            {isNetwork ? "Réseau STS · Artisan près de chez vous" : "Demande de devis · Sellerie sur-mesure"}
          </p>
          <h1 className="font-light text-3xl md:text-5xl leading-tight">
            {isNetwork ? (
              <>Décrivez votre projet, <br />
              <span className="font-serif italic text-[#C9B896]">un artisan STS vous répond sous 48&nbsp;h.</span></>
            ) : (
              <>Décris ton projet, <br />
              <span className="font-serif italic text-[#C9B896]">on te répond sous 48 h.</span></>
            )}
          </h1>
          <p className="text-[#E8DCC8]/85 font-light mt-4 max-w-2xl leading-relaxed">
            {isNetwork
              ? "Votre demande sera transmise au réseau STS. Un professionnel qualifié (formé par la Sellerie Académie) vous recontacte sous 48 h pour étudier votre projet."
              : "Automobile, moto, nautique, mobilier, médical… Notre atelier chiffre ton projet et te propose un devis personnalisé."
            }
          </p>
        </div>
      </section>

      {/* Progress dots */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10">
        <div className="flex items-center justify-between gap-2" data-testid="quote-progress">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center">
              <div className={`shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-colors ${
                i < stepIdx ? "bg-[#1F2A4A] border-[#1F2A4A] text-[#C9B896]"
                : i === stepIdx ? "bg-[#C9B896] border-[#C9B896] text-[#1F2A4A]"
                : "bg-transparent border-[#1F2A4A]/25 text-[#1F2A4A]/40"
              }`}>
                {i < stepIdx ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`ml-2 text-[10px] uppercase tracking-[0.2em] hidden md:inline ${i === stepIdx ? "text-[#1F2A4A] font-semibold" : "text-[#4A5568]/60"}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#1F2A4A]/12 mx-2 md:mx-3" />}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {step.key === "category" && (
          <StepCategory
            value={form.category}
            precision={form.category_precision}
            onSelect={(k) => setField("category", k)}
            onPrecisionChange={(v) => setField("category_precision", v)}
          />
        )}
        {step.key === "brief" && (
          <StepBrief form={form} setField={setField} />
        )}
        {step.key === "delivery" && (
          <StepDelivery form={form} setField={setField} />
        )}
        {step.key === "inspiration" && (
          <StepInspiration images={images} onImages={onImages} onRemove={removeImage} />
        )}
        {step.key === "contact" && (
          <StepContact form={form} setField={setField} />
        )}

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#1F2A4A]/12">
          <button
            type="button"
            onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
            disabled={stepIdx === 0}
            className="inline-flex items-center gap-2 text-sm text-[#1F2A4A] hover:text-[#C9B896] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="quote-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Précédent
          </button>
          {!isLast ? (
            <Button
              onClick={() => setStepIdx((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canNext}
              className="rounded-full px-8 h-11 bg-[#1F2A4A] hover:bg-[#141C2E] text-white disabled:opacity-40"
              data-testid="quote-next-btn"
            >
              Continuer <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!canNext || submitting}
              className="rounded-full px-8 h-11 bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A] font-semibold disabled:opacity-40"
              data-testid="quote-submit-btn"
            >
              {submitting ? "Envoi en cours…" : (<>Envoyer ma demande <Send className="w-4 h-4 ml-1.5" /></>)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Step components ─────────────────────────────────────────────────

const StepCategory = ({ value, precision, onSelect, onPrecisionChange }) => (
  <div>
    <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Quelle est <span className="font-serif italic">la nature du projet&nbsp;?</span></h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {CATEGORIES.map(({ key, label, subtitle, Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => onSelect(key)}
          className={`text-left rounded-xl border p-4 transition-all ${
            value === key ? "border-[#1F2A4A] bg-[#1F2A4A] text-[#FAF7F0] shadow-lg" : "border-[#1F2A4A]/15 bg-white hover:border-[#C9B896] hover:bg-[#FAF7F0]"
          }`}
          data-testid={`quote-cat-${key}`}
        >
          <Icon className={`w-5 h-5 mb-2 ${value === key ? "text-[#C9B896]" : "text-[#C9B896]"}`} strokeWidth={1.75} />
          <p className={`font-medium text-sm ${value === key ? "text-[#FAF7F0]" : "text-[#1F2A4A]"}`}>{label}</p>
          <p className={`text-xs mt-0.5 ${value === key ? "text-[#E8DCC8]/70" : "text-[#4A5568]"}`}>{subtitle}</p>
        </button>
      ))}
    </div>
    {value && (
      <label className="block mt-6">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Précisions (optionnel)</span>
        <input
          type="text"
          value={precision || ""}
          onChange={(e) => onPrecisionChange(e.target.value)}
          placeholder="Ex : marque, modèle, année, matière souhaitée…"
          className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]"
          data-testid="quote-precision-input"
        />
      </label>
    )}
  </div>
);

const StepBrief = ({ form, setField }) => (
  <div>
    <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Décris <span className="font-serif italic">ton projet.</span></h2>
    <label className="block mb-5">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Description détaillée *</span>
      <textarea
        value={form.project_description}
        onChange={(e) => setField("project_description", e.target.value)}
        rows={7}
        placeholder="Exemple : Refaire complètement les sièges avant + banquette arrière d'une Ford Mustang 1968 en cuir marron avec surpiqûres beige. J'ai les pièces d'origine à refaire à l'identique."
        className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 py-3 text-sm resize-y focus:outline-none focus:border-[#C9B896] leading-relaxed"
        data-testid="quote-description"
      />
      <span className="block text-[11px] text-[#4A5568]/70 mt-1">Minimum 15 caractères — plus tu es précis, plus le devis sera juste.</span>
    </label>
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Quantité (optionnel)</span>
      <input
        type="text"
        value={form.quantity}
        onChange={(e) => setField("quantity", e.target.value)}
        placeholder="Ex : 2 sièges avant + 1 banquette"
        className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]"
        data-testid="quote-quantity"
      />
    </label>
  </div>
);

const StepDelivery = ({ form, setField }) => (
  <div>
    <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Quand souhaites-tu <span className="font-serif italic">recevoir la sellerie&nbsp;?</span></h2>
    <label className="block mb-5">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Date souhaitée</span>
      <input
        type="date"
        value={form.delivery_date}
        onChange={(e) => setField("delivery_date", e.target.value)}
        className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]"
        data-testid="quote-delivery-date"
      />
    </label>
    <fieldset className="mb-5">
      <legend className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Flexibilité sur la date</legend>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { k: "strict", label: "Impérative" },
          { k: "flexible", label: "Flexible ±2 semaines" },
          { k: "no-rush", label: "Pas pressé" },
        ].map((o) => (
          <label
            key={o.k}
            className={`text-center rounded-lg border cursor-pointer text-sm py-2.5 transition-colors ${form.delivery_flexibility === o.k ? "border-[#1F2A4A] bg-[#1F2A4A] text-[#FAF7F0]" : "border-[#1F2A4A]/20 bg-white hover:border-[#C9B896]"}`}
          >
            <input type="radio" name="flex" value={o.k} checked={form.delivery_flexibility === o.k} onChange={(e) => setField("delivery_flexibility", e.target.value)} className="sr-only" data-testid={`quote-flex-${o.k}`} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Budget indicatif (optionnel)</span>
      <select
        value={form.budget_range}
        onChange={(e) => setField("budget_range", e.target.value)}
        className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]"
        data-testid="quote-budget"
      >
        <option value="">Je ne sais pas</option>
        <option value="< 500 €">Moins de 500 €</option>
        <option value="500-1500 €">500 – 1 500 €</option>
        <option value="1500-5000 €">1 500 – 5 000 €</option>
        <option value="5000-15000 €">5 000 – 15 000 €</option>
        <option value="> 15000 €">Plus de 15 000 €</option>
      </select>
    </label>
  </div>
);

const StepInspiration = ({ images, onImages, onRemove }) => (
  <div>
    <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-3">Images <span className="font-serif italic">d'inspiration.</span></h2>
    <p className="text-[#4A5568] font-light text-sm mb-6">
      Jusqu'à {MAX_IMAGES} photos (5 Mo max chacune, JPG · PNG · WEBP · HEIC). Optionnel.
    </p>

    <label className="block border-2 border-dashed border-[#C9B896]/60 rounded-xl bg-[#FAF7F0] hover:bg-[#F4EFE3] cursor-pointer py-10 text-center transition-colors" data-testid="quote-drop-zone">
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        multiple
        className="sr-only"
        onChange={(e) => onImages(e.target.files)}
      />
      <Upload className="w-6 h-6 text-[#C9B896] mx-auto mb-2" strokeWidth={2} />
      <p className="text-sm font-medium text-[#1F2A4A]">Cliquer pour ajouter des images</p>
      <p className="text-[11px] text-[#4A5568] mt-1">{images.length}/{MAX_IMAGES} images ajoutées</p>
    </label>

    {images.length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5" data-testid="quote-images-grid">
        {images.map((f, i) => (
          <div key={i} className="relative rounded-lg overflow-hidden bg-white border border-[#1F2A4A]/12 aspect-square">
            <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[#1F2A4A]/85 text-white flex items-center justify-center hover:bg-red-700"
              aria-label={`Retirer ${f.name}`}
              data-testid={`quote-image-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const StepContact = ({ form, setField }) => (
  <div>
    <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-6">Comment on te <span className="font-serif italic">recontacte&nbsp;?</span></h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Nom complet *</span>
        <input type="text" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]" data-testid="quote-name" />
      </label>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Email *</span>
        <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]" data-testid="quote-email" />
      </label>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Téléphone</span>
        <input type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]" data-testid="quote-phone" />
      </label>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Ville</span>
        <input type="text" value={form.city} onChange={(e) => setField("city", e.target.value)} className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]" data-testid="quote-city" />
      </label>
      <label className="block sm:col-span-2">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-[#4A5568] mb-2 font-semibold">Entreprise (optionnel)</span>
        <input type="text" value={form.company} onChange={(e) => setField("company", e.target.value)} className="w-full rounded-lg border border-[#1F2A4A]/20 bg-white px-4 h-11 text-sm focus:outline-none focus:border-[#C9B896]" data-testid="quote-company" />
      </label>
    </div>
    <p className="text-[11px] text-[#4A5568] mt-6">
      En envoyant ce formulaire, vous acceptez que vos données soient utilisées pour vous répondre. Aucune information n'est cédée à des tiers.
    </p>
  </div>
);

export default QuoteRequestPage;
